import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Segmented, Tooltip, Typography } from 'antd';

import TagSelect from '../tags/TagSelect';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { canWrite, canWriteCard } from '../../utils/permissions';
import { formatCount } from '../../utils/counts';
import { isRestrictedTag, tagIdOf, tagOwnerCollectionId, tagPathLabel } from '../../utils/tags';
import { canApplyByHand, managesTagOwner, privateFailureWords, privateTagRefusal } from '../../utils/itemTags';
import { itemTagTitleFor } from '../../utils/tagTitleMemory';
import { applyToIds, bulkApplyToFilter, dryRunIds, MAX_ROWS, newBatchId, rowErrorList } from '../../services/itemsBulk';
import { applyPrivateTags, removePrivateTags, TAG_APPLICATIONS_BATCH } from '../../services/tagApplications';

const { Text, Paragraph } = Typography;

/**
 * THE SELECTION BAR'S "Tags…" (docs/plans/tags.md §5.2; docs/platform-
 * standards.md § Batch actions): add or remove tags on every selected item,
 * as collection tags or as my private tags.
 *
 * - COLLECTION tags ride the bulk write (`POST /api/items/bulk`, `tagIdsAdd`
 *   / `tagIdsRemove`, judged explicitly): a restricted tag needs manage on
 *   its collection, a strict rule's tag is never added by hand, a deleted or
 *   foreign one is `unknown_tag` — each refusal against its row. Rows or the
 *   list's own filter ("all 412 matching"), dry-run chunk by chunk first,
 *   and only a clean check lights Apply (rule 1). A refused row is named and
 *   can be dropped from the selection — by id, or as `select.exclude` under
 *   "all matching" (rule 2).
 * - PRIVATE tags go through the person's own store (`PUT` / `DELETE
 *   /api/tag-applications`, read access is enough), 100 applications a
 *   batch. Check is the store's own DRY RUN (keepr-api, the tags rulings,
 *   b32), every batch of it: what would change comes from the server, and
 *   every refusal the real call would make is named against its item (a
 *   409's `failures[].index`, re-keyed from its batch) and can be dropped
 *   from the selection, as on the collection path — only a clean check
 *   lights Apply. The commit's batches stop at the first refusal — naming
 *   the items it is about, with the earlier batches applied (rule 3: the
 *   panel says the promise is weaker). A private tag goes on items one
 *   picks, never on "all matching" (the store takes item ids, and a filter
 *   holds none; ruled b36).
 *
 * Item tags are offered by title in both: by hand where an item can carry
 * one (the API judges each row's family), privately anywhere.
 */
export default function ItemsTagsPanel({
	open,
	onClose,
	collectionId,
	collection = null,
	cardsById = {},
	getElementsForCard,
	items = [],
	ids = [],
	allMatching = false,
	matchingCount = 0,
	excluded = [],
	filter = null,
	resolveLookupValue,
	onApplied,
	onDropIds,
}) {
	const [action, setAction] = useState('add');
	const [store, setStore] = useState('collection');
	const [tagIds, setTagIds] = useState([]);
	const [plan, setPlan] = useState(null);
	const [refusals, setRefusals] = useState([]);
	// Whether the refusals shown came from the check (nothing was saved) or
	// from the commit (on the private path: that batch was not saved).
	const [refusedAt, setRefusedAt] = useState('check');
	const [failure, setFailure] = useState(null);
	const [busy, setBusy] = useState(null);

	const count = allMatching ? Math.max(matchingCount - excluded.length, 0) : ids.length;
	const { tags: vocabulary } = useCollectionTags(collectionId, { enabled: open });
	const privateTags = useOptionalTags()?.tags || null;

	// Who may put collection tags on this selection at all.
	const writable = allMatching
		? canWrite(collection)
		: (items || []).some(item => !collection || canWriteCard(collection, normalizeId(item?.card_id)));
	const collectionReason = writable ? null : 'You can’t change these items, so only private tags can go on them.';
	const privateReason = allMatching ? 'Private tags go on items you pick. Select them on the list rather than all matching.' : null;

	// A store that cannot be used here is never the one in play.
	useEffect(() => {
		if (!open) return;
		if (store === 'collection' && collectionReason && !privateReason) setStore('private');
		if (store === 'private' && privateReason && !collectionReason) setStore('collection');
	}, [open, store, collectionReason, privateReason]);

	const resetPlan = useCallback(() => { setPlan(null); setRefusals([]); setRefusedAt('check'); setFailure(null); }, []);
	useEffect(() => { resetPlan(); }, [action, store, tagIds, ids.length, allMatching, excluded.length, resetPlan]);
	useEffect(() => { setTagIds([]); }, [store]);
	useEffect(() => {
		if (open) return;
		setAction('add'); setStore('collection'); setTagIds([]); resetPlan(); setBusy(null);
	}, [open, resetPlan]);

	const select = useMemo(() => ({
		...filter,
		expectedCount: matchingCount,
		...(excluded.length ? { exclude: excluded } : {}),
	}), [filter, matchingCount, excluded]);

	const patch = useMemo(() => {
		if (store !== 'collection' || !tagIds.length) return null;
		return action === 'add' ? { tagIdsAdd: tagIds } : { tagIdsRemove: tagIds };
	}, [store, tagIds, action]);

	const itemsById = useMemo(() => {
		const map = {};
		(items || []).forEach(item => { map[normalizeId(item?._id)] = item; });
		return map;
	}, [items]);

	const titleOf = useCallback(item => getItemDisplayValue({
		card: cardsById?.[normalizeId(item?.card_id)] || null,
		elementDefs: typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : [],
		item,
		resolveLookupValue,
	}), [cardsById, getElementsForCard, resolveLookupValue]);

	const tagName = useCallback(id => {
		const known = [...(vocabulary || []), ...(privateTags || [])].find(tag => tagIdOf(tag) === id);
		if (known) return tagPathLabel(known);
		return itemTagTitleFor(id)?.title || 'this tag';
	}, [vocabulary, privateTags]);

	const disabledFor = useCallback((tag, kind) => {
		if (kind !== 'collection') return null;
		if (action === 'add') return canApplyByHand(tag, { canEdit: true, collection }).reason;
		if (isRestrictedTag(tag) && !managesTagOwner(collection, tagOwnerCollectionId(tag))) return 'Restricted: only managers take it off items.';
		return null;
	}, [action, collection]);

	// ---- private: one application per item and tag ----

	const privateApplications = useMemo(() => {
		if (store !== 'private') return [];
		const out = [];
		ids.forEach(itemId => tagIds.forEach(tagId => out.push({ tagId, targetType: 'item', target_id: itemId })));
		return out;
	}, [store, ids, tagIds]);

	// A refused private application, named against its item (the selection
	// bar's refusal row, droppable by `_id`).
	const privateRefusalRow = useCallback((application, code) => ({
		_id: application.target_id,
		code,
		message: privateFailureWords(code),
		tagId: application.tagId,
	}), []);

	// ---- check, then apply ----

	const runCheck = useCallback(async () => {
		if (!tagIds.length) return;
		setFailure(null);
		setBusy('checking');
		try {
			if (store === 'private') {
				// The store's own dry run, every batch (b32): the server says what
				// changes and names every refusal; nothing is written.
				const check = action === 'add' ? applyPrivateTags : removePrivateTags;
				const result = await check(privateApplications, { dryRun: true });
				const named = (result?.refusals || []).map(entry => privateRefusalRow(entry.application, entry.code));
				setRefusals(named);
				setRefusedAt('check');
				setPlan(named.length ? null : {
					private: true,
					change: action === 'add' ? Number(result?.applied) || 0 : Number(result?.removed) || 0,
					unchanged: Number(result?.unchanged) || 0,
				});
			} else if (allMatching) {
				const result = await bulkApplyToFilter(collectionId, select, patch, { dryRun: true });
				setPlan({ summary: result?.summary, matched: result?.matched });
				setRefusals([]);
			} else {
				const result = await dryRunIds(collectionId, ids, patch);
				setPlan({ summary: result.summary, matched: ids.length });
				setRefusals(result.refusals);
			}
		} catch (error) {
			if (store === 'private') {
				setRefusals([]);
				setPlan(null);
				setFailure(privateTagRefusal(error));
			} else {
				const rows = rowErrorList(error);
				setRefusals(rows);
				setRefusedAt('check');
				setPlan(null);
				if (!rows.length) setFailure(errorMessageOf(error));
			}
		} finally {
			setBusy(null);
		}
	}, [tagIds, store, action, privateApplications, privateRefusalRow, allMatching, collectionId, select, patch, ids]);

	const runApply = useCallback(async () => {
		if (!tagIds.length) return;
		setBusy('saving');
		setFailure(null);
		try {
			if (store === 'private') {
				const send = action === 'add' ? applyPrivateTags : removePrivateTags;
				await send(privateApplications);
				onApplied?.({ ok: true, written: ids.map(_id => ({ _id, action: 'updated' })) }, { mode: 'tags', chunks: Math.ceil(privateApplications.length / TAG_APPLICATIONS_BATCH) });
			} else if (allMatching) {
				const result = await bulkApplyToFilter(collectionId, select, patch, { batchId: newBatchId() });
				onApplied?.(result, { mode: 'tags', chunks: 1 });
			} else {
				const result = await applyToIds(collectionId, ids, patch);
				onApplied?.(result, { mode: 'tags', chunks: result.chunks });
			}
			onClose?.();
		} catch (error) {
			if (store === 'private') {
				const batch = error?.failedBatch;
				const failures = Array.isArray(error?.response?.data?.failures) ? error.response.data.failures : [];
				const named = batch ? failures.map(entry => {
					const application = batch.applications[Number(entry.index)];
					return application ? privateRefusalRow(application, entry.code) : null;
				}).filter(Boolean) : [];
				setRefusals(named);
				setRefusedAt('apply');
				setPlan(null);
				const landed = batch ? batch.done * TAG_APPLICATIONS_BATCH : 0;
				setFailure(landed
					? `${formatCount(landed, 'change') || `${landed} changes`} landed before this stopped. Private tags go in batches of ${TAG_APPLICATIONS_BATCH}; each batch is all or nothing, and the earlier ones stay.`
					: (named.length ? null : errorMessageOf(error)));
			} else {
				setRefusals(rowErrorList(error));
				setRefusedAt('apply');
				setPlan(null);
				setFailure(error?.partialBatch
					? `${error.partialBatch.written.length} of ${count} items were changed before this failed. A selection this large is sent in batches, so it is not all or nothing across them.`
					: (rowErrorList(error).length ? null : errorMessageOf(error)));
			}
		} finally {
			setBusy(null);
		}
	}, [tagIds, store, action, privateApplications, privateRefusalRow, onApplied, ids, allMatching, collectionId, select, patch, onClose, count]);

	// ---- render ----

	const refusedIds = useMemo(() => Array.from(new Set(refusals.map(row => normalizeId(row?._id)).filter(Boolean))), [refusals]);
	const chunks = store === 'private'
		? Math.ceil(privateApplications.length / TAG_APPLICATIONS_BATCH)
		: Math.ceil((allMatching ? 0 : ids.length) / MAX_ROWS);
	const ready = tagIds.length > 0 && !(store === 'collection' && collectionReason) && !(store === 'private' && privateReason);

	const storeOption = (value, label, reason) => ({
		value,
		disabled: Boolean(reason),
		label: reason ? <Tooltip title={reason}><span>{label}</span></Tooltip> : label,
	});

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={`Tags · ${formatCount(count, 'item') || 'no items'}`}
			destroyOnClose
			width={560}
			footer={[
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				<Button key="check" onClick={runCheck} loading={busy === 'checking'} disabled={!ready || busy === 'saving'} data-action="tags-check">
					Check
				</Button>,
				<Button
					key="apply"
					type="primary"
					onClick={runApply}
					loading={busy === 'saving'}
					disabled={!ready || !plan || refusals.length > 0 || busy === 'checking'}
					data-action="tags-apply"
				>
					{action === 'add' ? 'Add tags' : 'Remove tags'}
				</Button>,
			]}
		>
			<div className="items-tags__controls">
				<Segmented
					aria-label="Add or remove"
					value={action}
					onChange={setAction}
					options={[{ value: 'add', label: 'Add' }, { value: 'remove', label: 'Remove' }]}
				/>
				<Segmented
					aria-label="Which tags"
					value={store}
					onChange={setStore}
					options={[
						storeOption('collection', 'Collection tags', collectionReason),
						storeOption('private', 'Private tags', privateReason),
					]}
				/>
			</div>
			<Paragraph type="secondary" className="items-tags__hint">
				{store === 'private'
					? 'Only you see private tags. Reading an item is enough to tag it.'
					: 'Everyone who can see these items sees their collection tags.'}
			</Paragraph>

			<TagSelect
				key={store}
				multiple
				value={tagIds}
				onChange={next => setTagIds(Array.isArray(next) ? next : [])}
				collectionTags={store === 'collection' ? vocabulary : []}
				privateTags={store === 'private' ? (privateTags || []) : null}
				disabledFor={disabledFor}
				placeholder={action === 'add' ? 'Tags to add' : 'Tags to remove'}
				aria-label={action === 'add' ? 'Tags to add' : 'Tags to remove'}
				className="items-tags__select"
			/>

			{chunks > 1 ? (
				<Alert
					type="info"
					showIcon
					className="items-apply__note"
					message={`This is sent in ${chunks} batches`}
					description="Every batch is checked before any of them is saved — but once saving starts, a failure part-way leaves the earlier batches written."
				/>
			) : null}

			{failure ? <Alert type="error" showIcon message={failure} className="items-apply__note" /> : null}

			{refusals.length ? (
				<Alert
					type="error"
					showIcon
					className="items-apply__note"
					message={`${refusals.length} ${refusals.length === 1 ? 'item cannot' : 'items cannot'} be changed — ${store === 'private' && refusedAt === 'apply' ? 'that batch was not saved' : 'nothing was saved'}`}
					description={(
						<>
							<ul className="items-apply__list" data-testid="items-tags-refusals">
								{refusals.slice(0, 8).map((row, index) => {
									const item = row?._id ? itemsById[normalizeId(row._id)] : null;
									return (
										<li key={`${row?._id || 'row'}-${index}`}>
											<Text strong>{item ? titleOf(item) : `Row ${(row?.index ?? 0) + 1}`}</Text>
											{' — '}
											{row?.message || row?.code}
										</li>
									);
								})}
								{refusals.length > 8 ? <li>…and {refusals.length - 8} more</li> : null}
							</ul>
							{refusedIds.length ? (
								<Button size="small" onClick={() => onDropIds?.(refusedIds)} data-action="tags-drop-refused">
									Remove {refusedIds.length === 1 ? 'it' : `those ${refusedIds.length}`} from the selection
								</Button>
							) : null}
						</>
					)}
				/>
			) : null}

			{plan && !refusals.length ? (
				<Alert
					type="success"
					showIcon
					className="items-apply__note"
					data-testid="items-tags-plan"
					message={describePlan(plan, { action, tagName, tagIds, count })}
				/>
			) : null}
		</Modal>
	);
}

function describePlan(plan, { action, tagName, tagIds = [], count = 0 }) {
	if (plan.private) {
		// The server's own answer (the dry run's counts of applications — one
		// per item and tag). One tag: items; several: tags across the items.
		const { change, unchanged } = plan;
		if (tagIds.length === 1) {
			const name = tagName(tagIds[0]);
			return action === 'add'
				? `${name} goes on ${formatCount(change, 'item') || 'no items'}${unchanged ? ` (${unchanged} already ${unchanged === 1 ? 'has' : 'have'} it)` : ''}.`
				: `${name} comes off ${formatCount(change, 'item') || 'no items'}${unchanged ? ` (${unchanged} ${unchanged === 1 ? 'doesn’t' : 'don’t'} have it)` : ''}.`;
		}
		const across = formatCount(count, 'item') || 'these items';
		return action === 'add'
			? `${formatCount(change, 'private tag') || 'No private tags'} will go on across ${across}${unchanged ? ` (${formatCount(unchanged, 'is', 'are')} there already)` : ''}.`
			: `${formatCount(change, 'private tag') || 'No private tags'} will come off across ${across}${unchanged ? ` (${formatCount(unchanged, 'wasn’t', 'weren’t')} there)` : ''}.`;
	}
	const n = (plan.summary?.update || 0) + (plan.summary?.create || 0);
	return `${formatCount(n, 'item') || 'Nothing'} will be changed.`;
}

function errorMessageOf(error) {
	const data = error?.response?.data;
	if (data?.code === 'count_mismatch') return `${data.message || 'The filter changed.'} Refresh the list and select again.`;
	if (data?.code === 'filter_too_large') return data.message || 'That filter matches too many items; narrow it.';
	return data?.message || error?.message || 'That could not be applied.';
}
